import { Directive } from "@app/components/directive";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IFileIdFilter } from "@app/filters/file-id.filter";
import { Dirs } from "@app/helpers/dirs";
import { DialogService } from "@app/services/dialog.service";
import { DrawerService } from "@app/services/drawer.service";
import { KbService } from "@app/services/kb.service";
import { UploadService } from "@app/services/upload.service";
import { DialogButton, eAlertType, IDialog, IMediaObject, InputDialogButton, IRootScope } from "@models";
import { icons } from "@tools";
import * as angular from "angular";

export interface IMediaManagerScope extends ng.IScope {
    isDialog: boolean;
    apiPath: string;
    rootDir: string;
    startPath: string;

    isReadOnly: boolean;

    addFolder: () => void;
    fileDoubleClicked: (file: IMediaObject) => void;
    open: (args: any) => void;
    upload: () => void;
    deleteItems: () => void;
    rename: () => void;
    refresh: () => void;
    files: IMediaObject[];
    // uploadOptions: kendo.ui.UploadOptions;
    // listSource: kendo.data.DataSource;
    crumbs: ICrumb[];
    crumbClick: (crumb: ICrumb) => void;
    selectedFiles: IMediaObject[];
    selectedFile: IMediaObject;
    multiple: boolean;

    uploadingFiles: any[];
    uploading: boolean;
    addBlank: (ext: string) => void;

    tracker: ng.IPromiseTracker; // tracker is passed in from the outside.  It should be used when making http calls
}

export interface ICrumb {
    url: string;
    name: string;
    icon: string;
}
    
@NgComponent({
    token: Token.KbMediaManager,
    dependencies: [
        Token.$Window,
        Token.$Http,
        Token.KbService,
        Token.DialogService,
        Token.$RootScope,
        Token.DrawerService,
        Token.UploadService,
        Token.$Q,
        Token.$Filter,
        Token.$Timeout,
    ]
})
export class KbMediaManager extends Directive {

    constructor(
        private $window: ng.IWindowService,
        private $http: ng.IHttpService,
        private kbService: KbService,
        private dialogService: DialogService,
        private $rootScope: IRootScope,
        private drawerService: DrawerService,
        private uploadService: UploadService,
        private $q: ng.IQService,
        private $filter: ng.IFilterService,
        private $timeout: ng.ITimeoutService) {

        super();

        this.restrict = "E";
        this.template = Dirs.component("kb-media-manager");
        this.scope = {
            dialog: "@",
            multiple: "=?",
            selectedFile: "=?",
            selectedFiles: "=?",
            filter: "@",
            apiPath: "@",
            rootDir: "@",
            startPath: "@",
            open: "&",
            tracker: "=?"
        };
        this.replace = true;
            
        this.link = (scope: IMediaManagerScope, element, atts: any) => {

            this.atts = atts;
            this.mscope = scope;
            if (!this.mscope.apiPath) this.mscope.apiPath = "api/media";
            if (!this.mscope.rootDir) this.mscope.rootDir = "media";

            if (typeof atts.multiple == "undefined") {
                scope.multiple = true;
            }
                
            scope.isDialog = (atts.dialog == "true");

            scope.isReadOnly = !this.$rootScope.user.canModifyMedia || this.$rootScope.environment != "dev";
                
            scope.deleteItems = () => {
                this.deleteSelectedListItems();
            };

            scope.refresh = () => {
                this.refreshList();
            };

            scope.rename = () => {
                this.rename();
            };

            scope.addFolder = () => {
                this.addFolder();
            };

            scope.fileDoubleClicked = (file: IMediaObject) => {
                // if this is a folder, then navigate to the folder.  If a file, then call the handler of the consumer
                if (!file.isFile) {
                    this.navigate(file.relativePath);
                } else {
                    if (scope.open) {
                        scope.open({ file }); // call the handler of the consumer
                    }
                }
            };

            scope.upload = (accept?: string) => {
                this.uploadService.promptUserToChooseFiles().then(files => {
                    this.onUpload({
                        files
                    });
                    // setTimeout(() =>
                    // {
                    let promises = [];
                    files.forEach(file => {
                        let p = this.uploadService.uploadFileToMedia(file, this.currentDir, this.mscope.apiPath);
                        promises.push(p.then(() => {
                            this.uploadProgress({
                                files: [file],
                                percentComplete: 100
                            });
                        }, () => {
                            this.uploadError({
                                files: [file]
                            });
                        }, (e: ProgressEvent) => {
                            this.uploadProgress({
                                files: [file],
                                percentComplete: ((e.loaded / e.total) * 100)
                            });
                        }));
                    });
                    this.$q.all(promises).finally(() => this.uploadComplete());
                    // }, 500);
                });
            };
                
            scope.crumbClick = crumb => {
                this.navigate(crumb.url);
            };

            scope.addBlank = ext => {
                this.dialogService.input({
                    msg: "Select a name for the new file",
                    inputType: "text",
                    value: "New File",
                    buttons: [
                        new InputDialogButton(loc.ok, icons.success, args => {
                            if (args.value) {
                                $http.post("/api/templates/blank", {
                                    relativePath: this.currentDir + args.value + "." + ext
                                }, { tracker: scope.tracker }).then(response => {
                                    this.refreshList();
                                });
                            }
                        }),
                        new InputDialogButton(loc.cancel, icons.cancel, args => {

                        })
                    ]
                });
            };

            scope.files = [];

            if (!scope.isDialog) {
                if ($rootScope.user.canModifyMedia && this.$rootScope.environment == "dev") {
                    let drAddFolder = drawerService.addDrawer(icons.addFolder, loc.addfolder, scope.addFolder);
                }
                    
                let drOpen = drawerService.addDrawer(
                    icons.open, loc.open, () => scope.fileDoubleClicked(this.selectedItem()));

                if ($rootScope.user.canModifyMedia && this.$rootScope.environment == "dev") {
                    let drUpload = drawerService.addDrawer(icons.upload, loc.upload, scope.upload);

                    let drDelete = drawerService.addDrawer(icons.deleter, loc.deleter, scope.deleteItems);
                    let drRename = drawerService.addDrawer(icons.rename, loc.rename, scope.rename);
                }
                let drRefresh = drawerService.addDrawer(icons.refresh, loc.refresh, scope.refresh);
            }

            // setup root crumb
            let startDir = scope.startPath ? this.getDirectory(scope.startPath) : "";
            this.navigate(startDir);
                
        };
    }

    private currentDir: string;
    private mscope: IMediaManagerScope;
    private uploadDialog: IDialog;
    private atts;
    
    private getChildren = (dir: string) => {
        let params = $.param($.extend({dir}, { filter: this.atts.filter }));
        let url = this.mscope.apiPath + "/children?" + params;
        this.$http.get<IMediaObject[]>(url, { tracker: this.mscope.tracker }).then(r => {
            this.mscope.files = r.data;
            this.$timeout(() => {
                if (this.mscope.startPath) {
                    let startFile = this.mscope.files.find(f => f.relativePath == this.mscope.startPath);
                    if (startFile) this.mscope.selectedFile = startFile;
                    this.mscope.startPath = null;
                }
            }, 0);
        });
    }

    private getDirectory = (path: string) => {
        let index = path.indexOf("/");
        if (index > -1) {
            return path.substr(0, index);
        }
        return "";
    }
       
    private navigate(dir: string) {
        this.currentDir = dir;
        this.refreshCrumbs(dir );
        this.getChildren(dir);
        // this.mscope.listSource.read({ id: dir });            
    }

    private refreshCrumbs(dir: string) {
        this.mscope.crumbs = [];

        this.mscope.crumbs.push({ url: "", name: this.mscope.rootDir, icon: icons.image } as ICrumb);

        let segments: string[] = dir.split("/");

        for (let index = 0; index < segments.length; index++) {
            let segment = segments[index];
            if (segment) {
                // calculate the absolute url of the segment
                let url: string = "";
                for (let i = 0; i <= index; i++) {
                    url += segments[i] + "/";
                }
                this.mscope.crumbs.push({ url, name: segment, icon: icons.crumb } as ICrumb);
            }
        }
    }

    private deleteSelectedListItems() {
        this.dialogService.dialog({
            content: loc.msg_deletemedia,
            buttons: [
                new DialogButton(loc.ok, icons.success, () => {
                    let ids: string[] = new Array();
                    let selected = this.getListViewSelectedItems();

                    selected.forEach(mobject => {
                        this.$http.delete(this.mscope.apiPath,
                            {
                                params: {
                                    relativePath: mobject.relativePath
                                },
                                headers:
                                {
                                    "Content-Type": "application/json"
                                }
                            });
                    });
                        
                    // delete the stuff directly in the source so the user doesn't have to wait
                    $.each(selected, (index: number, mobject: IMediaObject) => {
                        // now remove the item from the listview
                        this.mscope.files.remove(mobject);
                        // (<any>this.mscope.listSource).remove(mobject);
                    });
                }),
                new DialogButton(loc.cancel, icons.cancel, args => {
                })
            ]
                
        });
    }
        
    private rename() {
        let selectedItems = this.getListViewSelectedItems();
        if (selectedItems && selectedItems.length > 0) {
            let item = selectedItems[0];
            let msg = item.isFile ? loc.msg_renamefile : loc.msg_renamefolder;

            this.dialogService.input({
                msg,
                value: item.name,
                buttons: [
                    new InputDialogButton(loc.ok, icons.success, args => {
                        this.$http.post(this.mscope.apiPath + "/rename", {
                            relativePath: item.relativePath, newName: args.value
                        }, { tracker: this.mscope.tracker })
                            .then(() => {
                                this.refreshList();
                            }, (e) => {
                                this.dialogService.alert({
                                    msg: "Rename failed. Please ensure your filename does not contains slashes.",
                                    type: eAlertType.error
                                });
                            });
                    }),
                    new InputDialogButton(loc.cancel, icons.cancel, args => {
                    })

                ]

            });
            }
    }
        
    private addFolder() {
        this.dialogService.input({
            msg: loc.msg_renamefolder,
            value: loc.newfolder,
            buttons: [
                new InputDialogButton(loc.ok, icons.success, args => {
                    this.$http.post(this.mscope.apiPath + "/addfolder", {
                        dir: this.currentDir, folderName: args.value
                    }, { tracker: this.mscope.tracker })
                        .then(() => {
                            this.refreshList();
                        });
                }),
                new InputDialogButton(loc.cancel, icons.cancel, args => {
                })

            ]

        });
    }

    private getListViewDataItem($domItem: JQuery): IMediaObject {
        let index: number = $domItem.index();
        return null;
        // return this.mscope.listSource.view()[index];
    }

    private getListViewSelectedItems(): IMediaObject[] {
        if (this.mscope.multiple) {
            return this.mscope.selectedFiles;
        }
        return [this.mscope.selectedFile];
    }

    public selectedItem(): IMediaObject {
        let selected = this.getListViewSelectedItems();
        if (selected) {
            return selected[0];
        }
        return null;
    }

    private refreshList() {
        this.getChildren(this.currentDir);
    }

    private onUpload(e) {
        if (!this.mscope.uploadingFiles) this.mscope.uploadingFiles = [];

        angular.forEach(e.files, (item, index) => {
            item.percentComplete = 0;
            this.mscope.uploadingFiles.push(item);
        });

        if (!this.mscope.uploading) {
                
            this.uploadDialog = this.dialogService.dialog({
                template: Dirs.view("dialog-upload-files"),
                scope: this.mscope.$new(),
                buttons: [
                    new InputDialogButton(loc.ok, icons.success, args => {
                        this.mscope.uploadingFiles = null;
                        this.refreshList();
                    }, false) // hide the ok button by passing in visible = false
                ]
            });
                
        }

        this.mscope.uploading = true;
    }

    private uploadProgress(e) {
        $.each(e.files, (index, file) => {
            let $li = $("#uploading-list #" + ((this.$filter as any)("fileId") as IFileIdFilter)(file.name));
            file.percentComplete = e.percentComplete;
        });
    }

    private uploadError(e) {
        $.each(e.files, (index, file: File) => {
            let $li = $("#uploading-list #" + ((this.$filter as any)("fileId") as IFileIdFilter)(file.name));
            $li.prepend('<div class="kb-icon error"><div class="kb-icon__glyph">' + icons.error + "</div></div>");
        });
    }

    private uploadComplete(e?) {
        this.mscope.uploading = false;
        // show the buttons in the dialog now\
        this.uploadDialog.buttons[0].visible = true;
    }
}
